/**
 * Memory mode UX with two complementary lanes instead of OFF | RULES | REFERENCE in one rail.
 *
 * - **catalog**: Hold–Rules (Hold | Rules) + Hold–Reference (Hold | Reference). Exactly one lane
 *   can be „active“ (RULES vs REFERENCE). Both Hold ⇒ OFF for the artifact.
 * - **subscription**: Configure flow only — Rules | Reference (pick pipeline before tagging).
 */

/** Configure dialog — same pattern as catalog: capped height bar */
const SUBSCRIPTION_SEG_GROUP =
  'inline-flex h-7 min-h-7 max-h-7 items-stretch overflow-hidden rounded-lg border border-gray-200/80 bg-gray-200/90 p-px gap-px';

/** Data Plane catalog — segment height from padding + label (no fixed h-*) */
const CATALOG_SEG_GROUP =
  'inline-flex items-stretch overflow-hidden rounded border border-gray-200/80 bg-gray-200/90 p-px gap-px';

const SEG_BTN_ACTIVE = 'bg-white text-gray-900 shadow-sm';
const SEG_BTN_IDLE = 'text-gray-600 hover:text-gray-800';

const CATALOG_HOLD_SELECTED =
  'bg-red-600 text-white shadow-sm enabled:hover:bg-red-700 ring-1 ring-inset ring-red-700/30';
const CATALOG_HOLD_IDLE =
  'bg-white/90 text-gray-700 enabled:hover:bg-red-50 enabled:hover:text-red-800';
const CATALOG_LANE_SELECTED =
  'bg-emerald-600 text-white shadow-sm enabled:hover:bg-emerald-700 ring-1 ring-inset ring-emerald-700/30';
const CATALOG_LANE_IDLE =
  'bg-white/90 text-gray-700 enabled:hover:bg-emerald-50 enabled:hover:text-emerald-800';

/** Flat catalog segments: 0.25rem padding around label text */
const CATALOG_SEG_BTN_BASE =
  [
    'm-0 box-border appearance-none flex min-h-0 min-w-0 flex-1 items-center justify-center',
    'border-0 p-1 text-[9px] font-medium uppercase tracking-wide',
    'leading-tight transition-colors active:opacity-90',
  ].join(' ');
const CATALOG_SEG_FOCUS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500';

const SUB_SEG_BTN_FOCUS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1';

/**
 * @param {{
 *   variant?: 'catalog' | 'subscription',
 *   value: 'RULES' | 'REFERENCE' | 'SKILL' | 'OFF',
 *   onChange: (next: 'RULES' | 'REFERENCE' | 'SKILL' | 'OFF') => void,
 *   loading?: boolean,
 *   disabled?: boolean,
 *   idPrefix?: string,
 *   catalogLane?: 'rules' | 'reference' | 'skill',
 *   columnHeaderId?: string,
 * }} props
 * ``catalogLane`` / ``columnHeaderId``: used in the catalog table — one lane per cell; header id for radiogroup ``aria-labelledby``.
 */
export default function MemoryLanePairControl({
  variant = 'catalog',
  value,
  onChange,
  loading = false,
  disabled = false,
  idPrefix = 'mem-lane',
  catalogLane,
  columnHeaderId,
}) {
  if (variant === 'subscription') {
    const modes = ['RULES', 'REFERENCE'];
    const labels = { RULES: 'Rules', REFERENCE: 'Reference' };
    const titles = {
      RULES: 'Directives (RULES) — short text injected into instructions.',
      REFERENCE:
        'Knowledge (REFERENCE) — large documents retrieved via semantic search.',
    };
    const v =
      value === 'REFERENCE'
        ? 'REFERENCE'
        : 'RULES';
    return (
      <div
        className={SUBSCRIPTION_SEG_GROUP}
        role="radiogroup"
        aria-label="Memory mode for this upload"
        aria-busy={loading}
        aria-disabled={disabled || loading}
      >
        {modes.map((m) => {
          const selected = v === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={selected}
              id={`${idPrefix}-sub-${m}`}
              title={titles[m]}
              disabled={disabled || loading}
              onClick={() => !loading && !disabled && onChange?.(m)}
              className={[
                'm-0 box-border appearance-none flex h-full min-h-0 min-w-[4rem] flex-1 items-center justify-center',
                'border-0 px-2 py-0 text-[10px] font-semibold uppercase tracking-wide leading-tight',
                'rounded-md transition-colors',
                SUB_SEG_BTN_FOCUS,
                selected ? SEG_BTN_ACTIVE : SEG_BTN_IDLE,
                disabled || loading ? 'cursor-not-allowed opacity-90' : 'cursor-pointer enabled:hover:bg-gray-950/10',
              ].join(' ')}
            >
              {labels[m]}
            </button>
          );
        })}
      </div>
    );
  }

  const rulesHold = value !== 'RULES';
  const rulesActive = value === 'RULES';
  const refHold = value !== 'REFERENCE';
  const refActive = value === 'REFERENCE';
  // Skill lane — same Hold/Active pattern as RULES; a skill is just another
  // variant of RULES (always-on, RULES-grade). Active value is 'SKILL'.
  const skillHold = value !== 'SKILL';
  const skillActive = value === 'SKILL';

  const pair = (
    lane,
    holdSelected,
    activeSelected,
    holdLabel,
    activeLabel,
    activeValue,
    labelledBy,
  ) => (
    <div
      className={CATALOG_SEG_GROUP}
      role="radiogroup"
      aria-label={labelledBy ? undefined : lane}
      aria-labelledby={labelledBy || undefined}
      aria-busy={loading}
    >
      <button
        type="button"
        role="radio"
        aria-checked={holdSelected}
        id={`${idPrefix}-${lane}-hold`}
        title={
          lane === 'Rules lane'
            ? 'Hold: inactive for Rules path. From Rules or Reference, returning here clears all mount tags.'
            : lane === 'Skill lane'
              ? 'Hold: this skill will NOT inject for any agent (even where subscribed). Switch to Skill to re-enable.'
              : 'Hold: inactive for Reference path. From Rules or Reference, returning here clears all mount tags.'
        }
        disabled={disabled || loading}
        onClick={() => !loading && !disabled && onChange?.('OFF')}
        className={[
          CATALOG_SEG_BTN_BASE,
          CATALOG_SEG_FOCUS,
          holdSelected ? CATALOG_HOLD_SELECTED : CATALOG_HOLD_IDLE,
          disabled || loading ? 'cursor-not-allowed opacity-90' : 'cursor-pointer',
        ].join(' ')}
      >
        {holdLabel}
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={activeSelected}
        id={`${idPrefix}-${lane}-act`}
        title={
          activeValue === 'RULES'
            ? 'Directives in hot memory (Dynamo).'
            : activeValue === 'SKILL'
              ? 'Skill active: injects as always-on RULES-grade context for its subscribed agents.'
              : 'Knowledge in cold vectors.'
        }
        disabled={disabled || loading}
        onClick={() => !loading && !disabled && onChange?.(activeValue)}
        className={[
          CATALOG_SEG_BTN_BASE,
          CATALOG_SEG_FOCUS,
          activeSelected ? CATALOG_LANE_SELECTED : CATALOG_LANE_IDLE,
          disabled || loading ? 'cursor-not-allowed opacity-90' : 'cursor-pointer',
        ].join(' ')}
      >
        {activeLabel}
      </button>
    </div>
  );

  if (catalogLane === 'rules') {
    return (
      <div className="inline-flex items-center" aria-disabled={disabled || loading}>
        {pair(
          'Rules lane',
          rulesHold,
          rulesActive,
          'Hold',
          'Rules',
          'RULES',
          columnHeaderId,
        )}
      </div>
    );
  }

  if (catalogLane === 'reference') {
    return (
      <div className="inline-flex items-center" aria-disabled={disabled || loading}>
        {pair(
          'Reference lane',
          refHold,
          refActive,
          'Hold',
          'Ref',
          'REFERENCE',
          columnHeaderId,
        )}
      </div>
    );
  }

  if (catalogLane === 'skill') {
    return (
      <div className="inline-flex items-center" aria-disabled={disabled || loading}>
        {pair('Skill lane', skillHold, skillActive, 'Hold', 'Skill', 'SKILL', columnHeaderId)}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-disabled={disabled || loading}>
      {pair('Rules lane', rulesHold, rulesActive, 'Hold', 'Rules', 'RULES')}
      {pair('Reference lane', refHold, refActive, 'Hold', 'Ref', 'REFERENCE')}
    </div>
  );
}
